import { useState, useRef, useCallback } from 'react';

/**
 * Hook that turns a ref'd element into a file drop zone.
 *
 * @param {object}   opts
 * @param {function} opts.onFiles  – called with an array of File objects when drop occurs
 * @param {boolean}  [opts.disabled] – when true the drop zone is inactive
 * @returns {{ isDragging, dropzoneHandlers }}
 */
export default function useComposerDropzone({ onFiles, disabled = false } = {}) {
  const [isDragging, setIsDragging] = useState(false);
  const counterRef = useRef(0); // nested drag enter/leave counter

  const handleDragEnter = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    // Only react to file drags, not text selections
    if (e.dataTransfer?.types?.includes('Files')) {
      counterRef.current += 1;
      if (counterRef.current === 1) setIsDragging(true);
    }
  }, [disabled]);

  const handleDragOver = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    counterRef.current -= 1;
    if (counterRef.current <= 0) {
      counterRef.current = 0;
      setIsDragging(false);
    }
  }, [disabled]);

  const handleDrop = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    counterRef.current = 0;
    setIsDragging(false);

    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length && onFiles) onFiles(files);
  }, [disabled, onFiles]);

  const dropzoneHandlers = {
    onDragEnter: handleDragEnter,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  };

  return { isDragging, dropzoneHandlers };
}
